import React, { useState } from 'react';
import Button from '../ui/Button';
import { FormInput } from '../FormField';
import ProcSection from './ProcSection';
import { formatDate } from '../../utils/formatDate';
import type { ParcelRow } from '../../types/Procurement';

interface Props {
  parcels: ParcelRow[];
  canEdit: boolean;
  onAdd: (input: { tracking_no: string; courier?: string }) => Promise<void>;
}

/** Domestic courier tracking numbers (parcels) of one order. */
const OrderParcels: React.FC<Props> = ({ parcels, canEdit, onAdd }) => {
  const [trackingNo, setTrackingNo] = useState('');
  const [courier, setCourier] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    if (!trackingNo.trim()) { setError('Enter the tracking number.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onAdd({ tracking_no: trackingNo.trim(), ...(courier.trim() ? { courier: courier.trim() } : {}) });
      setTrackingNo('');
      setCourier('');
    } catch {
      setError('The tracking number could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProcSection title="Parcels and tracking">
      {parcels.length === 0 ? (
        <p className="text-subtle">No tracking numbers yet.</p>
      ) : (
        <div className="proc-scroll">
          <table className="proc-table">
            <thead><tr><th>Tracking number</th><th>Courier</th><th>Domestic status</th><th>Last update</th><th>Agent status</th></tr></thead>
            <tbody>
              {parcels.map(p => (
                <tr key={p.id}>
                  <td>{p.tracking_no}</td>
                  <td>{p.courier ?? '—'}</td>
                  <td>{p.domestic_status ?? '—'}</td>
                  <td>{p.last_domestic_update ? formatDate(p.last_domestic_update, '') || p.last_domestic_update : '—'}</td>
                  <td>{p.agent_status ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && (
        <>
          <div className="form-row">
            <FormInput label="Tracking number" value={trackingNo} onChange={e => setTrackingNo(e.target.value)} />
            <FormInput label="Courier" value={courier} onChange={e => setCourier(e.target.value)} />
          </div>
          <div className="proc-section-actions">
            <Button variant="secondary" size="sm" loading={saving} onClick={() => void add()}>Add tracking number</Button>
          </div>
          {error && <p role="alert" className="form-error-text">{error}</p>}
        </>
      )}
    </ProcSection>
  );
};

export default OrderParcels;
